import type { TaskCategoryCode } from '@liza/fhir-utils';
import { TrendChart } from '@liza/ui/components/atoms/TrendChart';
import { cn } from '@liza/ui/utils';
import { formatDateOmitCurrentYear } from '@liza/ui/utils/date';
import { displayUnit, formatVitalValueParts } from '@liza/ui/utils/observation';
import { getTaskCategoryColor } from '@liza/ui/utils/ringCategories';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { CarePlanItemSecondary } from './CarePlanItemSecondary';
import { MeasurementValue } from './MeasurementValue';
import { useTimelinePlayback } from './TimelinePlaybackContext';
import { useCarePlanTitleClamp } from './CarePlanRowExpansionContext';

export type MeasurementResultItemProps = {
  className?: string;
  /** Observation display name (e.g. "Blood pressure"). */
  name: string;
  /** All readings for this measurement, used to draw the trend sparkline. */
  results?: WithId<Observation>[];
  /** Latest reading, used for the value and timestamp on the right. */
  latest?: WithId<Observation>;
  /** Human-readable schedule (e.g. "Daily (morning)"); omitted for as-needed / unscheduled. */
  scheduleLabel?: string;
  /** Optional task instructions shown beneath the name. */
  instructions?: string;
  /** Task category driving the icon fill; measurements fold to the clinical ring color. */
  category?: TaskCategoryCode | null;
  /** Localized unit that replaces the persisted (English) unit on the latest reading; used for
   * lifestyle measurements. Undefined leaves the observation's own unit unchanged. */
  unitOverride?: string;
  /**
   * Compact topic-card variant: the schedule (under the name), the special instructions (full width),
   * and the reading's unit + timestamp all collapse and only unfurl while the card is hovered — so the
   * resting row is just name · sparkline · value. Off by default, so the detail overlay and patient-web
   * show everything inline.
   */
  compact?: boolean;
  /**
   * Set the latest value in the destructive color when the lab flagged it or it falls outside its
   * reference range. Off by default: a care-plan row is a patient's own reading against a schedule,
   * with no range to be outside of. The labs view turns it on so a panel's flag survives expanding
   * the category, matching the collapsed line's condensed reading.
   */
  flagAbnormal?: boolean;
};

/**
 * A single row in the "Results" section of a CarePlan topic card: the measurement name on the left,
 * the trend sparkline and the latest value with its unit + timestamp beneath it on the right, and the
 * instructions/schedule unfurling full-width beneath. The enclosing group in `Measurements` owns the
 * click target that opens the edit dialog; the detail overlay uses `MeasurementDetailItem` instead,
 * which renders a large labeled chart.
 */
export function MeasurementResultItem({
  className,
  name,
  results,
  latest,
  scheduleLabel,
  instructions,
  category,
  unitOverride,
  compact,
  flagAbnormal,
}: MeasurementResultItemProps): React.ReactElement {
  const titleClamp = useCarePlanTitleClamp();
  const { t } = useTranslation('records');
  // Freeze the sparkline's draw animation while the timeline is scrubbing/playing (currentTime set), so
  // the reveal doesn't redraw it from scratch on every step; animate normally when live.
  const { currentTime } = useTimelinePlayback();
  const { value, unit: rawUnit } = latest
    ? formatVitalValueParts(latest, t('unknown'))
    : { value: '—', unit: undefined };
  const unit = displayUnit(!!latest, unitOverride, rawUnit);
  const categoryColor = getTaskCategoryColor(category);
  const hasChart = !!results && results.length >= 2;
  const dateLabel = latest?.effectiveDateTime ? formatDateOmitCurrentYear(latest.effectiveDateTime) : undefined;
  const reading = [unit, dateLabel].filter(Boolean).join(' · ') || undefined;

  return (
    <div
      data-care-plan-item
      className={cn(
        // The sparkline and value columns are fixed shares of the row (floored and capped) rather than
        // sized to their content, so they line up from row to row and widen with the container.
        'grid grid-cols-[minmax(0,1fr)_minmax(4rem,min(20%,10rem))_minmax(6rem,min(22%,12rem))] items-start gap-x-2.5 px-1 py-0.5 text-sm',
        className,
      )}
    >
      {/* Title with its schedule beneath it (schedule collapses until hover); clamped to two lines. */}
      <div className="flex min-w-0 flex-col">
        <span className={cn(titleClamp, 'font-medium')}>{name}</span>
        {scheduleLabel && (
          <CarePlanItemSecondary compact={compact}>
            <span className="text-xs text-muted-foreground">{scheduleLabel}</span>
          </CarePlanItemSecondary>
        )}
      </div>

      {/* Sparkline, centered on the value's line. */}
      <div className="h-6 w-full self-center">
        {hasChart && (
          // Fall back to the muted token (not TrendChart's primary default) so an uncategorized
          // row's sparkline matches its neutral icon surface rather than reading as an accent.
          <TrendChart
            results={results}
            className="size-full"
            color={categoryColor ?? 'var(--color-muted-foreground)'}
            animate={currentTime === null}
          />
        )}
      </div>

      {/* The value with its unit/timestamp beneath. */}
      <div className="flex min-w-0 flex-col items-end text-right">
        <MeasurementValue
          value={value}
          latest={latest}
          flagAbnormal={flagAbnormal}
          className="text-base/6"
          testId="measurement-result-value"
        />
        {reading && (
          <CarePlanItemSecondary compact={compact}>
            <span className="text-xs text-muted-foreground">{reading}</span>
          </CarePlanItemSecondary>
        )}
      </div>

      {/* Only the special instructions span the full width beneath (collapsed until hover). */}
      {instructions && (
        <div className="col-span-3 col-start-1">
          <CarePlanItemSecondary compact={compact}>
            <span className="text-xs text-muted-foreground">{instructions}</span>
          </CarePlanItemSecondary>
        </div>
      )}
    </div>
  );
}
